import { apiClient } from '@/api/client';
import type { OrganizationProfile, TeamInvitation, TeamMember } from '@/features/team/types';

type Rows<T> = T[] | { data?: T[]; items?: T[] };
const rows = <T>(value: Rows<T> | null | undefined): T[] =>
  !value ? [] : Array.isArray(value) ? value : value.data ?? value.items ?? [];

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const tenantPath = (tenantId: string, rest: string) => `/tenants/${encodeURIComponent(tenantId)}/${rest}`;

/**
 * Read-only organization-service calls for the Team & organization page (all existing,
 * permission-checked gateway routes: members.list / invite.list need member:read, org.get needs
 * org:read; every route pins :tenantId to the caller's token). Responses are mapped to an explicit
 * allow-list of fields, so server-side fields such as an invitation's token hash never enter app state.
 */
export const teamApi = {
  members: async (tenantId: string): Promise<TeamMember[]> => {
    const { data } = await apiClient.get<Rows<Record<string, unknown>>>(tenantPath(tenantId, 'members'));
    return rows(data).map((m) => ({
      id: String(m.id ?? m.userId ?? ''),
      userId: String(m.userId ?? ''),
      roleKey: String(m.roleKey ?? 'read_only'),
      isOwner: m.isOwner === true,
      status: String(m.status ?? 'active'),
      joinedAt: str(m.createdAt),
    }));
  },

  invitations: async (tenantId: string): Promise<TeamInvitation[]> => {
    const { data } = await apiClient.get<Rows<Record<string, unknown>>>(tenantPath(tenantId, 'invitations'));
    return rows(data).map((i) => ({
      id: String(i.id ?? ''),
      email: String(i.email ?? ''),
      roleKey: String(i.roleKey ?? 'read_only'),
      status: String(i.status ?? 'pending'),
      expiresAt: str(i.expiresAt),
      createdAt: str(i.createdAt),
    }));
  },

  organization: async (tenantId: string): Promise<OrganizationProfile> => {
    const { data } = await apiClient.get<Record<string, unknown>>(tenantPath(tenantId, 'organization'));
    const settings = (data.settings ?? {}) as Record<string, unknown>;
    const branding = (data.branding ?? {}) as Record<string, unknown>;
    return {
      id: String(data.id ?? ''),
      legalName: String(data.legalName ?? ''),
      displayName: str(branding.displayName),
      accountCode: String(data.accountCode ?? ''),
      countryCode: str(data.countryCode),
      vatNumber: str(data.vatNumber),
      supportEmail: str(settings.supportEmail),
      enforceMfa: settings.enforceMfa === true,
      createdAt: str(data.createdAt),
    };
  },
};
